import React, { useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";

const ReStoreDonateItems = () => {
  useEffect(() => {
    // Ensure the ReSupply widget loads
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://widget.resupplyapp.com/prod/loader.js";
    script.setAttribute("widget_code", "9b98287dd80a486f3d28743ede5dcfbd");
    script.onload = () => console.log("ReSupply widget loaded");
    document.body.appendChild(script);
  }, []);

  return (
    <>
      <Helmet>
        <title>Donate Items - Habitat Buffalo ReStore</title>
        <meta
          name="description"
          content="Donate goods to the Habitat Buffalo ReStore to help us build houses and support our mission. Check the list of items we accept and schedule a pickup today."
        />
      </Helmet>
      <Container>
        <h1>Donate Items to the Habitat Buffalo ReStore</h1>
        <Paragraph>
          Thank you for thinking of the Habitat Buffalo ReStore with your
          donation! Donating goods to a Habitat Buffalo ReStore helps us build
          houses and support our mission. We accept a wide range of items and
          prefer that donations be dropped off directly at our ReStore
          locations. However, we also offer a pickup service for your
          convenience.
        </Paragraph>
        <Paragraph>
          <strong>
            Please review our list of accepted items below before dropping off
            your donation. We accept drop-offs at both our South Buffalo and
            Tonawanda locations Tuesday through Saturday between 10:00 a.m. and
            4:30 p.m.
          </strong>
        </Paragraph>

        <Section>
          <h2 className="section-header green">Items We Accept</h2>
          <Paragraph>We accept a wide range of items, including:</Paragraph>
          <List>
            <ListItem>Furniture (in good condition)</ListItem>
            <ListItem>Appliances (10 years or newer)</ListItem>
            <ListItem>Cabinets and Countertops</ListItem>
            <ListItem>Doors (solid & hollow-core)</ListItem>
            <ListItem>Windows (double-pane vinyl, undamaged)</ListItem>
            <ListItem>Lighting and Electrical fixtures</ListItem>
            <ListItem>Plumbing fixtures and fittings</ListItem>
            <ListItem>Building materials</ListItem>
            <ListItem>Tools and Equipment</ListItem>
            <ListItem>Lawn, Garden, and Seasonal items</ListItem>
            <ListItem>Antiques, Art, and Home Décor</ListItem>
          </List>
        </Section>

        <Section>
          <h2 className="section-header orange">Items We Do Not Accept</h2>
          <List>
            <ListItemX>Used mattresses</ListItemX>
            <ListItemX>Hazardous materials</ListItemX>
            <ListItemX>Items in poor condition</ListItemX>
            <ListItemX>Frameless mirrors or glass</ListItemX>
            <ListItemX>Entertainment centers</ListItemX>
            <ListItemX>Large desks, medical equipment, or water beds</ListItemX>
            <ListItemX>Used paint, stain, or solvent</ListItemX>
            <ListItemX>Flammable or combustible chemicals</ListItemX>
          </List>
          <Paragraph>
            If you have an item that you don't see listed here, please contact
            us and we will review your donation.
          </Paragraph>
        </Section>

        {/* Donation Options */}
        <Section>
          <CardContainer>
            <Card>
              <CardTitle>For Individuals</CardTitle>
              <CardDescription>
                If you are looking to make a one-time, personal donation to our
                ReStores.
              </CardDescription>
              <Button href="https://onlinedonationpickup.com/habitatbuffalo/#intro">
                Donate Items
              </Button>
            </Card>
            <Card>
              <CardTitle>For Organizations</CardTitle>
              <CardDescription>
                Organizations can donate large quantities of items to our
                ReStores.
              </CardDescription>
              <Button href="/form/donation">Donate as an Organization</Button>
            </Card>
          </CardContainer>

          {/* Premier Pickup Service */}
          <PremierPickupCard>
            <NewBadge>NEW</NewBadge>
            <CardTitle>Premier Pickup Service</CardTitle>
            <CardDescription>
              <strong>Premium Paid Pickup Service</strong> – In partnership with{" "}
              <strong>ReSupply</strong>, our Premier Pickup Service offers a
              faster, full-service donation pickup experience. Most pickups are
              scheduled within a few days, and items can be collected from any
              floor in your home. This service is available even outside our
              normal pickup area and accepts all items, including those we
              typically cannot take. Every pickup supports Habitat Buffalo’s
              mission to build homes for families in need.
            </CardDescription>
            <PremierButton
              href="https://donor.resupplyapp.com/start/charity/114?utm_source=widget"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule a Pickup
            </PremierButton>
          </PremierPickupCard>
        </Section>
      </Container>

      {/* Floating Button */}
      <FloatingButton
        href="https://donor.resupplyapp.com/start/charity/114?utm_source=widget"
        target="_blank"
        rel="noopener noreferrer"
      >
        Schedule a Pickup
      </FloatingButton>
    </>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-top: 40px;
`;

const List = styled.ul`
  columns: 2;
  -webkit-columns: 2;
  -moz-columns: 2;
  list-style-type: none;
  padding: 0;

  @media (max-width: 768px) {
    columns: 1;
    -webkit-columns: 1;
    -moz-columns: 1;
  }
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 25px;
  margin-bottom: 10px;

  &::before {
    content: "✔";
    position: absolute;
    left: 0;
    color: var(--primary-color);
  }
`;

const ListItemX = styled.li`
  position: relative;
  padding-left: 25px;
  margin-bottom: 10px;

  &::before {
    content: "✘";
    position: absolute;
    left: 0;
    color: var(--hhb-red);
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 40px;
`;

const Card = styled.div`
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  width: 300px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 1.5em;
  margin-bottom: 10px;
`;

const CardDescription = styled.p`
  font-size: 1em;
  margin-bottom: 20px;
`;

const Button = styled.a`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: var(--accent-color);
  }
`;

const FloatingButton = styled.a`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #ff9900;
  color: white;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  transition: background 0.3s ease, transform 0.2s;
  z-index: 1000;

  &:hover {
    background-color: #e68a00;
    transform: scale(1.05);
  }
`;

const PremierPickupCard = styled.div`
  background-color: #ffffff;
  border: 2px solid #ff9900;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 0 20px rgba(255, 153, 0, 0.7);
  margin-top: 20px;
  position: relative;
`;

const NewBadge = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  background-color: #ff0000;
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 50px;
  transform: rotate(-10deg);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
`;

const PremierButton = styled.a`
  background-color: #ff9900;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 5px;
  margin-top: 15px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  transition: background 0.3s ease;

  &:hover {
    background-color: #e68a00;
  }
`;

export default ReStoreDonateItems;
